// Dependencies
import React, {
  FC,
  useState,
  useEffect,
  createRef,
  ReactText,
  useRef,
} from "react";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Axios from "axios";
import ReactCrop from "react-image-crop";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons";

// Components

import UploadImage from "../../Components/UploadImage/UploadImage";
import Modal from "../../Components/Modal/Modal";
import Button from "../../Components/Button/Button";
import Form from "../../Components/Form/Form";

// Images and Styles
import "react-image-crop/dist/ReactCrop.css";
import "../../css/Dashboard.scss";
import "react-toastify/dist/ReactToastify.css";
import profileSVG from "./../../media/undraw_profile.cac3f1f0.svg";

// Utils
import { getCroppedImg } from "../../utils/crop";
import { formatAddress } from "../../utils/formatter";
import {
  getSize,
  getImage,
  getDimensions,
  getb64,
} from "../../utils/imageProperties";
import PhotoBoothModal from "../../Components/Modal/PhotoBoothModal";

const Dashboard = ({ student: { patronid, email, firstName, lastName } }) => {
  const canvasRef = createRef<HTMLCanvasElement>();
  const [originalImage, setOriginalImage] = useState("");
  const [preview, setPreview] = useState("");
  const [image, setImage] = useState({ selected: profileSVG, temp: "" });
  const [uploadModal, setUploadModal] = useState({
    open: false,
    loading: true,
  });
  const [mailModal, setMailModal] = useState(false);
  const [cropperModal, setCropperModal] = useState(false);
  const [dimensions, setDimensions] = useState("");
  const [checkbox, setCheckbox] = useState("pickup");
  const [formValues, setFormValues] = useState({
    address: "",
    city: "",
    state: "",
    zipcode: 0,
    photo_id: "",
  });
  const [size, setSize] = useState(0);
  const [crop, setCrop] = useState({
    width: 30,
    height: 30,
    x: 0,
    y: 0,
    unit: "%" as "px" | "%",
  });
  const [dashboard, setDashboard] = useState({
    photoInReview: false,
    canSubmit: false,
    hasCropped: false,
  });
  //let [imageRef, setImageRef] = useState();
  let imageRef = useRef<HTMLImageElement>(null);
  let [correctAddressModal, setCorrectAddressModal] = useState(false);
  let [adddress, setAddress] = useState({
    address: "",
    city: "",
    state: "",
    zipcode: 0,
  });
  // let [isMailFormFilled, setIsMailFormFilled] = useState(false);

  let fileUrl: any;

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      await Axios.all([
        Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/mysql-check-image-exists.php?patronid=${patronid}`
        ),
        Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/mysql-get-image.php?patronid=${patronid}`
        ),
        Axios.get(
          `https://icatcard.ucmerced.edu/auth/caticlick/oci-get-address.php?patronid=${patronid}`
        ),
      ]).then(
        Axios.spread((imageExists, profileImage, addressResponse): void => {
          if (imageExists.data === "profile image under review") {
            setDashboard({ ...dashboard, photoInReview: true });
            photoInReviewWarning();
          }

          setImage({ selected: profileImage.data, temp: profileImage.data });
          const [address, city, state, zipcode] =
            addressResponse.data.split(",");
          setAddress({ address, city, state, zipcode: zipcode?.split("-")[0] });
        })
      );
    };

    fetchData();
  }, []);

  const makeClientCrop = async (crop): Promise<void> => {
    if (imageRef && crop.width && crop.height) {
      const croppedImageUrl = await getCroppedImg(
        imageRef.current,
        crop,
        `${patronid}.jpeg`,
        fileUrl
      );
      getb64(croppedImageUrl as string, image, setImage);
    }
  };

  const photoInReviewWarning = (): ReactText => {
    return toast.info(
      (): JSX.Element => (
        <div
          className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3"
          role="alert"
        >
          <p className="font-bold">
            Your photo has been submitted already! You will receive an email
            after the CatCard office has finished reviewing your new photo.
          </p>
          <footer className="flex justify-center">Tap to close.</footer>
        </div>
      ),
      {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      }
    );
  };

  const incompleteForm = (): ReactText => {
    return toast.info(
      (): JSX.Element => (
        <div
          className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3"
          role="alert"
        >
          <p className="font-bold">
            Please make sure all fields have been filled before submitting.
          </p>
          <footer className="flex justify-center">Tap to close.</footer>
        </div>
      ),
      {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      }
    );
  };

  const duplicatePhotoUpload = (): ReactText => {
    return toast.info(
      (): JSX.Element => (
        <div
          className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3"
          role="alert"
        >
          <p className="font-bold">
            Your Government issued ID or your High School issued ID is the same
            as your new photo that you are trying to upload. Please make sure
            you picked the correct photo and that they are different.
          </p>
          <footer className="flex justify-center">Tap to close.</footer>
        </div>
      ),
      {
        position: "top-right",
        autoClose: 10000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      }
    );
  };

  const submissionError = (): ReactText => {
    return toast.info(
      (): JSX.Element => (
        <div
          className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3"
          role="alert"
        >
          <p className="font-bold">
            <span style={{ fontSize: "2rem" }}>⚠️</span> We are sorry, but there
            was an error and we could not process your request at this moment.
            Please try again at another time or email CatCard at <br />
            <u>
              <a href="mailto:catcard@ucmerced.edu">catcard@ucmerced.edu</a>
            </u>
          </p>
          <footer className="flex justify-center">Tap to close.</footer>
        </div>
      ),
      {
        position: "top-left",
        autoClose: 10000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      }
    );
  };

  const upload = (): void => {
    const { address, state, city, zipcode, photo_id } = formValues;

    // Check if form values are complete
    if (
      address === "" ||
      state === "" ||
      city === "" ||
      zipcode < 0 ||
      photo_id === ""
    ) {
      incompleteForm();
      return;
    }

    // Validate patronid
    if (
      patronid.toString().length < 0 ||
      patronid.toString() === "0" ||
      patronid < 0 ||
      !patronid.toString().match(/\d+/)
    ) {
      setDashboard({ ...dashboard, canSubmit: false });
      submissionError();
      return;
    }

    // Debugging: Ensure image is actually being submitted
    console.log("Submitting Image Data:", {
      image: image.selected,
      patronid,
      dimensions,
      size,
      checkbox: checkbox === "mail" ? 8 : 9,
    });

    if (dashboard.canSubmit) {
      Axios.post(
        `https://icatcard.ucmerced.edu/auth/caticlick/mysql-insert-image.php`,
        {
          image:
            image.selected.split(":")[0] === "blob"
              ? image.temp
              : image.selected,
          patronid,
          dimensions,
          size,
          checkbox: checkbox === "mail" ? 8 : 9,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000,
        }
      )
        .then((_response): void => {
          console.log("✅ Image submitted successfully!");
          setDashboard({ ...dashboard, photoInReview: true });
        })
        .catch((error) => {
          console.error("❌ Error submitting image:", error);
          toast.error("Failed to upload image. Please try again.");
        });

      Axios.post(
        `https://icatcard.ucmerced.edu/auth/caticlick/mysql-insert-mail-info.php`,
        {
          patronid,
          address,
          city,
          state,
          zipcode,
          photo_id,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 30000,
        }
      )
        .then((_response) => console.log("✅ Address submitted successfully!"))
        .catch((error) => console.error("❌ Error submitting address:", error));
    }
  };

  return (
    <main className="flex flex-col justify-center items-center">
      {!dashboard.photoInReview && image.selected !== "" ? (
        <div className="absolute" style={{ top: "50%", right: "3%" }}></div>
      ) : null}
      <img
        className="profile-image rounded"
        src={image.selected === "" ? profileSVG : image.selected}
        alt="profile"
      />
      <section className="button-container">
        {dashboard.photoInReview ? (
          <div>
            <div className="flex flex-col justify-center align-center text-center text-lg">
              <b>Thank you for submitting a photo!</b> You will receive an email
              after the CatCard office has finished reviewing your new photo.
              <span>
                For any questions or concerns email{" "}
                <u>
                  <a href="mailto:catcard@ucmerced.edu">catcard@ucmerced.edu</a>
                </u>
              </span>
            </div>
            <div className="flex justify-center items-center">
              <Button
                onClick={(): void => {
                  window.location.replace("https://catcard.ucmerced.edu");
                }}
              >
                Logout
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex flex-col items-center justify-around">
              <Button
                onClick={(): void => {
                  //notify();
                  setUploadModal({ open: true, loading: false });
                }}
                disabled={dashboard.photoInReview}
              >
                {image.selected === ""
                  ? "Select a Photo"
                  : "Change Selected Photo"}
              </Button>
              {/* Mail out check box */}
              {originalImage !== "" ? (
                <div className="flex flex-col items-center">
                  <h3 className="text-xl">
                    <b>Step 2: Verify your identity.</b>
                  </h3>
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      className="form-checkbox text-pink-600"
                      onChange={(): void => {
                        setCorrectAddressModal(true);
                        setCheckbox("mail");
                      }}
                    />
                    <span className="ml-2">
                      &nbsp;Check the box to provide us with your current
                      mailing address and ID Verification.
                    </span>
                  </label>
                  {/* or */}
                  {/* Pick Up check box */}
                  {/* <label className="inline-flex items-center">
                                        <input
                                            type="checkbox"
                                            className="form-checkbox text-pink-600"
                                            onChange={(): void => {
                                                setCheckbox('pickup');
                                            }}
                                            checked={checkbox === 'pickup' ? true : false}
                                        />
                                        <span className="ml-2">&nbsp;Pick up CatCard ID on campus.</span>
                                    </label> */}
                </div>
              ) : null}
              {formValues.address !== "" ||
              formValues.state !== "" ||
              formValues.city !== "" ||
              formValues.zipcode > 0 ||
              formValues.photo_id !== "" ? (
                <Button
                  onClick={upload}
                  disabled={!image.selected || dashboard.photoInReview}
                >
                  Step 3: Submit Photo
                </Button>
              ) : null}
            </div>
          </div>
        )}
        {[
          "lsevanto",
          "ahernandez-perez2",
          "jgermino",
          "rbellofatto",
          "rbraden",
          "erahilly",
          "dgallegosgutierrez",
          "jbair2",
          "elandaverry",
          'cvalentine2',
          "awu92",
          "aschweickert",
          "brusson",
          "dlo6",
          "echang52",
          "jamesqian",
          "jvaleriano2",
          "jsalgado19",
          "mcavoy-jensen",
          "mmubako",
          "nbaba",
          "jbarrera41",
        ].includes(email.split("@")[0]) ? (
          <Link to="/caticlick/admin">
            <Button
              onClick={(): null => {
                return null;
              }}
            >
              Admin Dashboard
            </Button>
          </Link>
        ) : null}
      </section>
      {/* Photo Booth Modal */}
      <PhotoBoothModal
        open={uploadModal.open}
        didSetImage={(newImage: string): void => {
          console.log("📸 Image captured:", newImage);
          setImage({ selected: newImage, temp: newImage });
          getSize(newImage, setSize);
          setOriginalImage(newImage);
          setDashboard({ ...dashboard, canSubmit: true });
        }}
        didClose={() => {
          setUploadModal({ ...uploadModal, open: false });
        }}
        patronID={patronid}
      />

      {/* Upload Image Modal */}
      {/** 
            <Modal
                show={uploadModal.open}
                submitButtonText="Done"
                handleSubmit={(): void => {
                    getDimensions(image.selected, setDimensions);
                    getSize(image.selected, setSize);
                    setUploadModal({ ...uploadModal, open: false });
                    setOriginalImage(image.selected);
                }}
                handleClose={(): void => {
                    if (originalImage === '') {
                        setImage({ selected: '', temp: '' });
                    }
                    setPreview('');
                    setUploadModal({ ...uploadModal, open: false });
                }}
            >
                <UploadImage
                    canvasRef={canvasRef}
                    isLoading={uploadModal.loading}
                    image={(e: string) => {
                        setImage({ selected: getImage(e), temp: getImage(e) });
                    }}
                    submit={(): void => {
                        setDashboard({ ...dashboard, canSubmit: true });
                    }}
                    preview={preview}
                    setPreview={setPreview}
                />
            </Modal>
            */}
      {/* Crop the Image that was Uploaded */}
      <Modal
        show={cropperModal}
        submitButtonText="Crop"
        handleSubmit={(): void => {
          getDimensions(image.temp, setDimensions);
          getSize(image.temp, setSize);
          setCropperModal(false);
          setImage({ ...image, selected: image.temp });
        }}
        handleClose={(): void => {
          setCropperModal(false);
          setCrop({
            unit: "%" as "px" | "%",
            width: 30,
            height: 30,
            x: 0,
            y: 0,
          });
          setImage({
            ...image,
            temp: image.selected,
          });
        }}
      >
        {
          <ReactCrop
            crop={crop}
            ruleOfThirds
            onComplete={(crop): void => {
              makeClientCrop(crop);
              console.log(image.selected === image.temp);
              setDashboard({ ...dashboard, hasCropped: true });
            }}
            onChange={(_crop: any, percentCrop: any): void => {
              setCrop(percentCrop);
            }}
            className="m-4"
          >
            <img ref={imageRef} src={originalImage} />
          </ReactCrop>
        }
      </Modal>
      {/* Modal to input information regarding where to send CatCard */}
      <Modal
        show={mailModal}
        handleClose={(): void => {
          setMailModal(false);
        }}
        submitButtonText="Submit"
        handleSubmit={(): void => {
          const { address, state, city, zipcode, photo_id } = formValues;

          if (
            address === "" ||
            state === "" ||
            city === "" ||
            zipcode < 0 ||
            photo_id === ""
          ) {
            incompleteForm();
          } else if (originalImage === formValues.photo_id) {
            duplicatePhotoUpload();
          } else {
            setFormValues({
              address: formatAddress(address),
              city,
              state,
              zipcode,
              photo_id,
            });
            setMailModal(false);
          }
        }}
      >
        <Form values={formValues} setValues={setFormValues} />
      </Modal>
      <Modal
        show={correctAddressModal}
        handleClose={(): void => {
          setCorrectAddressModal(false);
          setMailModal(true);
        }}
        submitButtonText="Yes"
        handleSubmit={(): void => {
          setCorrectAddressModal(false);
          setFormValues({ photo_id: "", ...adddress });
          setMailModal(true);
        }}
      >
        <>
          <h2>
            <b>Is this your current mailing address?</b>
          </h2>
          <div className="flex flex-col py-5">
            <p>{adddress.address}</p>
            <p>
              {adddress.city}, {adddress.state}, {adddress.zipcode}
            </p>
          </div>
        </>
      </Modal>
    </main>
  );
};

export default Dashboard;
